import { scopeType } from "../models/TrendingModelsV2";

/**
 * Get the duration in milliseconds for a given scope/period
 */
export const getScopeDuration = (scope: scopeType): number | null => {
	switch (scope) {
		case "1 min":
			return 1 * 60 * 1000;
		case "10 min":
			return 10 * 60 * 1000;
		case "30 min":
			return 30 * 60 * 1000;
		case "1 hour":
			return 1 * 60 * 60 * 1000;
		case "4 hours":
			return 4 * 60 * 60 * 1000;
		case "12 hours":
			return 12 * 60 * 60 * 1000;
		case "1 day":
			return 24 * 60 * 60 * 1000;
		case "3 days":
			return 3 * 24 * 60 * 60 * 1000;
		case "10 days":
			return 10 * 24 * 60 * 60 * 1000;
		case "custom":
			return null; // Custom doesn't have a fixed duration
		default:
			return null;
	}
};

/**
 * Calculate the end date based on start date and period
 */
export const calculateEndFromStart = (
	start: Date,
	scope: scopeType,
): Date | null => {
	const duration = getScopeDuration(scope);
	if (duration === null) return null;

	return new Date(start.getTime() + duration);
};

/**
 * Calculate the start date based on end date and period
 */
export const calculateStartFromEnd = (
	end: Date,
	scope: scopeType,
): Date | null => {
	const duration = getScopeDuration(scope);
	if (duration === null) return null;

	return new Date(end.getTime() - duration);
};

export const clampScopeToNow = (
	start: Date,
	end: Date,
	scope: scopeType,
): { start: Date; end: Date } => {
	const now = Date.now();

	if (end.getTime() <= now) {
		return { start, end };
	}

	const duration = getScopeDuration(scope);

	if (duration !== null) {
		return { start: new Date(now - duration), end: new Date(now) };
	}

	if (start.getTime() >= now) {
		return {
			start: new Date(now - (end.getTime() - start.getTime())),
			end: new Date(now),
		};
	}

	return { start, end: new Date(now) };
};

/**
 * Calculate the window of the period length that ends at the given date,
 * a custom period falls back to the span it currently has
 */
export const calculateWindowEndingAt = (
	end: Date,
	scope: scopeType,
	currentSpan: number,
): { start: Date; end: Date } | null => {
	const duration = getScopeDuration(scope) ?? currentSpan;
	if (!Number.isFinite(duration) || duration <= 0) return null;

	return { start: new Date(end.getTime() - duration), end };
};

/**
 * Calculate both start and end dates to maintain the period duration
 * when one of them changes
 */
export const calculateCounterpartDate = (
	changedDate: Date,
	changedField: "start" | "end",
	scope: scopeType,
): { start: Date; end: Date } | null => {
	const duration = getScopeDuration(scope);

	// If custom scope, don't calculate counterpart
	if (duration === null) {
		return null;
	}

	if (changedField === "start") {
		return {
			start: changedDate,
			end: new Date(changedDate.getTime() + duration),
		};
	} else {
		return {
			start: new Date(changedDate.getTime() - duration),
			end: changedDate,
		};
	}
};
